import { useEffect, useState } from "react";
import FileUpload from "components/FileUpload";
import { API_ENDPOINTS } from "lib/apiUrl";
import { useProjectFormContext } from "pages/project/context/ProjectFormContext";

function isFileObject(value) {
  return typeof File !== "undefined" && value instanceof File;
}

/** FileUpload row shape from persisted `{ name, url }` entries. */
function offeringDocumentsToUploadItems(docs) {
  if (!Array.isArray(docs)) return [];
  return docs
    .filter((d) => String(d?.url ?? "").trim() || String(d?.name ?? "").trim())
    .map((d, i) => {
      const url = String(d?.url ?? "").trim();
      const name = String(d?.name ?? "Document").trim() || "Document";
      if (url) {
        return {
          id: `offering-doc-${i}-${url.slice(0, 48)}`,
          name,
          file: url,
          status: "complete",
          progress: 100,
        };
      }
      return {
        id: `offering-doc-pending-${i}-${name.replaceAll(/\s+/g, "-")}`,
        name,
        file: null,
        status: "idle",
        progress: 0,
      };
    });
}

function uploadItemsToOfferingDocuments(items) {
  return (items || [])
    .map((item) => {
      const url =
        typeof item.file === "string"
          ? String(item.file).trim()
          : String(item?.url ?? "").trim();
      const name =
        (isFileObject(item.file) && item.file.name) ||
        String(item?.name ?? "").trim() ||
        "Document";
      return { name, url };
    })
    .filter((d) => String(d.name).trim() || String(d.url).trim());
}

export default function StepOfferingDocument() {
  const { values, setField, errors } = useProjectFormContext();
  const [uploadItems, setUploadItems] = useState(() =>
    offeringDocumentsToUploadItems(values.offeringDocuments),
  );

  useEffect(() => {
    setUploadItems((prev) => {
      if (prev.some((p) => isFileObject(p?.file))) return prev;
      return offeringDocumentsToUploadItems(values.offeringDocuments);
    });
  }, [values.offeringDocuments]);

  const handleItemsChange = (items) => {
    setUploadItems(items);
    setField("offeringDocuments", uploadItemsToOfferingDocuments(items));
  };

  return (
    <div className="transition-opacity duration-300">
      {errors.offeringDocuments ? (
        <p className="mb-3 text-xs font-medium text-red-600" role="alert">
          {errors.offeringDocuments}
        </p>
      ) : (
        <p className="mb-3 text-xs text-text-500">
          Upload the private placement memorandum, term sheet, or other offering documents (PDF
          recommended). Use the upload icon on each row to save the file and capture its URL.
        </p>
      )}
      <FileUpload
        title="Offering documents"
        accept=".pdf,.doc,.docx"
        multiple
        maxSizeMB={15}
        helperText="PDF or Word — up to 15 MB each. Use the upload icon on each row to obtain a URL."
        uploadPath={API_ENDPOINTS.uploadDocument}
        items={uploadItems}
        onItemsChange={handleItemsChange}
        showPreviewActions
        showPreviewProgress
      />
    </div>
  );
}
